import { RESET_SELECTION, RESET_STANDARD } from "~/features/configurator/common/advancedTranslationUtils";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import type { ConfiguratorDefinition } from "~/features/configurator/common/v2/StepDefinition";
import * as RailCstic from "~/features/configurator/Rail/v2/configuration/classic/RailsCstics";
import { userIsCustomer } from "~/domains/user/utils/roles";
import { getCsticNumericValue, getCsticsValue } from "~/features/configurator/common/v2/helper";
import { RESET_STANDARD_AND_LENGTH } from "~/features/configurator/Linear/advancedTranslationsTransformer";
import {
  type AlarHelperTextFactoryParams,
  ExtraConfig,
  HelperCstics,
  InputCstics,
} from "~/features/configurator/Rail/components/ConfigureRailSubstep/constants";

export const RailsDefinition: ConfiguratorDefinition = {
  description: [
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[RailCstic.Series]?.value === "TSX";
      },
      value: "configurator.rail.summary.TSX.description",
    },
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[RailCstic.Series]?.value === "TKSD";
      },
      value: "configurator.rail.summary.TKSD.description",
    },
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[RailCstic.Series]?.value === "TKVD";
      },
      value: "configurator.rail.summary.TKVD.description",
    },
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[RailCstic.Series]?.value === "TKVD..-W";
      },
      value: "configurator.rail.summary.TKVD-W.description",
    },
  ],
  mediaUrl: [
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[RailCstic.Series]?.value === "TSX";
      },
      value: "configurator.rail.summary.TSX.image",
    },
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[RailCstic.Series]?.value === "TKSD";
      },
      value: "configurator.rail.summary.TKSD.image",
    },
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[RailCstic.Series]?.value === "TKVD";
      },
      value: "configurator.rail.summary.TKVD.image",
    },
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[RailCstic.Series]?.value === "TKVD..-W";
      },
      value: "configurator.rail.summary.TKVD-W.image",
    },
  ],
  pages: [
    {
      name: "configurator.rail.page.1.name",
      enabled: true,
      resetLabel: RESET_SELECTION,
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.Series,

          name: "configurator.rail.step.series.name",
          description: "configurator.rail.step.series.description",

          tiles: [
            {
              name: "TSX",
              description: "configurator.rail.cstic.series.TSX.description",
              imageUrl: "configurator.rail.cstic.series.TSX.image",
              info: "configurator.rail.cstic.series.TSX.info",
              cstics: [
                {
                  csticName: RailCstic.Series,
                  value: "TSX",
                },
              ],
            },
            {
              name: "TKSD",
              description: "configurator.rail.cstic.series.TKSD.description",
              imageUrl: "configurator.rail.cstic.series.TKSD.image",
              info: "configurator.rail.cstic.series.TKSD.info",
              cstics: [
                {
                  csticName: RailCstic.Series,
                  value: "TKSD",
                },
              ],
            },
            {
              name: "TKVD",
              description: "configurator.rail.cstic.series.TKVD.description",
              imageUrl: "configurator.rail.cstic.series.TKVD.image",
              info: "configurator.rail.cstic.series.TKVD.info",
              cstics: [
                {
                  csticName: RailCstic.Series,
                  value: "TKVD",
                },
              ],
            },
            {
              name: "TKVD..-W",
              description: "configurator.rail.cstic.series.TKVD-W.description",
              imageUrl: "configurator.rail.cstic.series.TKVD-W.image",
              info: "configurator.rail.cstic.series.TKVD-W.info",
              cstics: [
                {
                  csticName: RailCstic.Series,
                  value: "TKVD..-W",
                },
              ],
            },
          ],
          tilesPerRow: 4,

          csticsToReset: [RailCstic.Series],

          visible: true,
        },
        {
          type: SubstepType.Size,

          name: "configurator.rail.step.size.name",
          description: "configurator.rail.step.size.description",

          tiles: [
            {
              name: "15",
              cstics: [
                {
                  csticName: RailCstic.Size,
                  value: "15",
                },
              ],
            },
            {
              name: "20",
              cstics: [
                {
                  csticName: RailCstic.Size,
                  value: "20",
                },
              ],
            },
            {
              name: "25",
              cstics: [
                {
                  csticName: RailCstic.Size,
                  value: "25",
                },
              ],
            },
            {
              name: "30",
              cstics: [
                {
                  csticName: RailCstic.Size,
                  value: "30",
                },
              ],
            },
            {
              name: "35",
              cstics: [
                {
                  csticName: RailCstic.Size,
                  value: "35",
                },
              ],
            },
            {
              name: "45",
              cstics: [
                {
                  csticName: RailCstic.Size,
                  value: "45",
                },
              ],
            },
            {
              name: "55",
              cstics: [
                {
                  csticName: RailCstic.Size,
                  value: "55",
                },
              ],
            },
            {
              name: "65",
              cstics: [
                {
                  csticName: RailCstic.Size,
                  value: "65",
                },
              ],
            },
            {
              name: "100",
              cstics: [
                {
                  csticName: RailCstic.Size,
                  value: "100",
                },
              ],
            },
          ],
          tilesPerRow: 12,

          csticsToReset: [RailCstic.Size],

          visible: true,
        },
      ],
    },
    {
      name: "configurator.rail.page.2.name",
      enabled: true,
      resetLabel: RESET_STANDARD,
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.Coating,

          name: "configurator.rail.step.coating.name",
          description: "configurator.rail.step.coating.description",
          info: "configurator.rail.step.coating.info",
          footerInfo: [
            [
              {
                condition: ({ mainKmat }) => {
                  const [series, size, accuracy, coating] = getCsticsValue(
                    mainKmat,
                    RailCstic.Series,
                    RailCstic.Size,
                    RailCstic.Accuracy,
                    RailCstic.Coating,
                  );

                  return (
                    series === "TSX" &&
                    ["RROC", "KD"].includes(coating) &&
                    ((["25", "35", "45", "55", "65"].includes(size) && ["G0", "G1", "G2", "G3"].includes(accuracy)) ||
                      (size === "100" && accuracy === "G2"))
                  );
                },
                value: "configurator.rail.step.accuracy.footerInfo",
              },
            ],
            "configurator.rail.step.coating.footerInfo",
          ],

          tiles: [
            {
              name: "configurator.rail.cstic.coating./.name",
              cstics: [
                {
                  csticName: RailCstic.Coating,
                  value: "/",
                },
              ],
            },
            {
              name: "configurator.rail.cstic.coating.RROC.name",
              description: "configurator.rail.cstic.coating.RROC.description",
              info: "configurator.rail.cstic.coating.RROC.info",
              cstics: [
                {
                  csticName: RailCstic.Coating,
                  value: "RROC",
                },
              ],
            },
            {
              name: "configurator.rail.cstic.coating.KD.name",
              description: "configurator.rail.cstic.coating.KD.description",
              info: "configurator.rail.cstic.coating.KD.info",
              cstics: [
                {
                  csticName: RailCstic.Coating,
                  value: "KD",
                },
              ],
            },
          ],
          tilesPerRow: 4,

          csticsToReset: [RailCstic.Coating],

          visible: true,
        },
        {
          type: SubstepType.Accuracy,

          name: "configurator.rail.step.accuracy.name",
          description: "configurator.rail.step.accuracy.description",
          info: [
            {
              condition: ({ mainKmat }) => {
                const [series] = getCsticsValue(mainKmat, RailCstic.Series);
                return ["TSX"].includes(series);
              },
              value: "configurator.rail.step.accuracy.info.TSX",
            },
            {
              condition: ({ mainKmat }) => {
                const [series] = getCsticsValue(mainKmat, RailCstic.Series);
                return ["TKSD"].includes(series);
              },
              value: "configurator.rail.step.accuracy.info.TKSD",
            },
            {
              condition: ({ mainKmat }) => {
                const [series] = getCsticsValue(mainKmat, RailCstic.Series);
                return ["TKVD", "TKVD..-W"].includes(series);
              },
              value: "configurator.rail.step.accuracy.info.TKVD",
            },
          ],

          tiles: [
            {
              name: "G0",
              cstics: [
                {
                  csticName: RailCstic.Accuracy,
                  value: "G0",
                },
              ],
            },
            {
              name: "G1",
              cstics: [
                {
                  csticName: RailCstic.Accuracy,
                  value: "G1",
                },
              ],
            },
            {
              name: "G2",
              cstics: [
                {
                  csticName: RailCstic.Accuracy,
                  value: "G2",
                },
              ],
            },
            {
              name: "G3",
              cstics: [
                {
                  csticName: RailCstic.Accuracy,
                  value: "G3",
                },
              ],
            },
            {
              name: "G4",
              cstics: [
                {
                  csticName: RailCstic.Accuracy,
                  value: "G4",
                },
              ],
            },
          ],
          tilesPerRow: 6,

          footerInfo: [
            [
              {
                condition: ({ mainKmat }) => {
                  const [series, size, accuracy, coating] = getCsticsValue(
                    mainKmat,
                    RailCstic.Series,
                    RailCstic.Size,
                    RailCstic.Accuracy,
                    RailCstic.Coating,
                  );
                  return (
                    (series === "TSX" &&
                      ["25", "35", "45", "55", "65"].includes(size) &&
                      ["G0", "G1"].includes(accuracy) &&
                      ["/", "RROC", "KD"].includes(coating)) ||
                    (series === "TSX" && size === "100" && accuracy === "G3" && ["RROC", "KD"].includes(coating)) ||
                    (series === "TSX" && size === "100" && ["G0", "G1", "G2"].includes(accuracy) && coating === "/")
                  );
                },
                value: "configurator.rail.step.accuracy.footerInfo",
              },
            ],
          ],

          csticsToReset: [RailCstic.Accuracy],

          visible: true,
        },
      ],
    },
    {
      name: "configurator.rail.page.3.name",
      enabled: true,
      resetLabel: RESET_STANDARD,
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.Mounting,
          visible: true,

          name: "configurator.rail.step.mounting.name",

          tiles: [
            {
              name: "configurator.rail.cstic.mounting./.name",
              info: "configurator.rail.cstic.mounting./.info",
              imageUrl: "configurator.rail.cstic.mounting./.imageUrl",
              cstics: [
                {
                  csticName: RailCstic.MountingHoleType,
                  value: "/",
                },
              ],
            },
            {
              name: "configurator.rail.cstic.mounting.U.name",
              info: "configurator.rail.cstic.mounting.U.info",
              imageUrl: "configurator.rail.cstic.mounting.U.imageUrl",
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    const [series, sizeCode] = getCsticsValue(mainKmat, RailCstic.Series, RailCstic.Size);
                    if (series === "TSX" && sizeCode === "100") {
                      return true;
                    }
                    if (series === "TKSD" && ["30", "35", "45"].includes(sizeCode)) {
                      return true;
                    }
                    return false;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
              overrideCsticsToEnable: true,
              cstics: [
                {
                  csticName: RailCstic.MountingHoleType,
                  value: "U",
                },
                {
                  csticName: RailCstic.ClosingPlugs,
                  value: "/",
                },
                {
                  csticName: RailCstic.CounterboreChamfered,
                  value: "N",
                },
                [
                  {
                    condition: ({ mainKmat }) => {
                      return mainKmat.cstics[RailCstic.GuidewayDesign]?.value === "ADE";
                    },
                    value: {
                      csticName: RailCstic.GuidewayDesign,
                      value: "/",
                    },
                  },
                ],
              ],
            },
          ],
          tilesPerRow: 4,

          csticsToReset: [RailCstic.MountingHoleType],
        },
        {
          type: SubstepType.Closure,
          visible: [
            {
              condition: ({ mainKmat }) => {
                return mainKmat.cstics[RailCstic.MountingHoleType]?.value !== "U";
              },
              value: true,
            },
          ],

          name: "configurator.rail.step.closure.name",
          info: "configurator.rail.step.closure.info",

          groupedTiles: [
            {
              name: "configurator.rail.step.closure.section.plugs.name",
              visible: true,
              tiles: [
                {
                  name: "configurator.rail.cstic.closure.KA-TN.name",
                  description: "configurator.rail.cstic.closure.KA-TN.description",
                  imageUrl: "configurator.rail.cstic.closure.KA-TN.imageUrl",
                  info: "configurator.rail.cstic.closure.KA-TN.info",
                  cstics: [
                    {
                      csticName: RailCstic.ClosingPlugs,
                      value: "KA..TN",
                    },
                  ],
                },
                {
                  name: "configurator.rail.cstic.closure.KA-TN/A.name",
                  description: "configurator.rail.cstic.closure.KA-TN/A.description",
                  imageUrl: "configurator.rail.cstic.closure.KA-TN/A.imageUrl",
                  info: "configurator.rail.cstic.closure.KA-TN/A.info",
                  cstics: [
                    {
                      csticName: RailCstic.ClosingPlugs,
                      value: "KA..TN/A",
                    },
                  ],
                },
                {
                  name: "configurator.rail.cstic.closure.KA-TN/B.name",
                  description: "configurator.rail.cstic.closure.KA-TN/B.description",
                  imageUrl: "configurator.rail.cstic.closure.KA-TN/B.imageUrl",
                  info: "configurator.rail.cstic.closure.KA-TN/B.info",
                  cstics: [
                    {
                      csticName: RailCstic.ClosingPlugs,
                      value: "KA..TN/B",
                    },
                  ],
                },
                {
                  name: "configurator.rail.cstic.closure.KA-M.name",
                  description: "configurator.rail.cstic.closure.KA-M.description",
                  imageUrl: "configurator.rail.cstic.closure.KA-M.imageUrl",
                  info: [
                    {
                      condition: ({ mainKmat }) => {
                        const [series] = getCsticsValue(mainKmat, RailCstic.Series);
                        return ["TSX"].includes(series);
                      },
                      value: "configurator.rail.cstic.closure.KA-M.info.TSX",
                    },
                    {
                      condition: () => true,
                      value: "configurator.rail.cstic.closure.KA-M.info.other",
                    },
                  ],
                  cstics: [
                    {
                      csticName: RailCstic.ClosingPlugs,
                      value: "KA..M",
                    },
                  ],
                },
                {
                  name: "configurator.rail.cstic.closure.KA-M/A.name",
                  description: "configurator.rail.cstic.closure.KA-M/A.description",
                  imageUrl: "configurator.rail.cstic.closure.KA-M/A.imageUrl",
                  info: "configurator.rail.cstic.closure.KA-M/A.info",
                  cstics: [
                    {
                      csticName: RailCstic.ClosingPlugs,
                      value: "KA..M/A",
                    },
                  ],
                },
                {
                  name: "configurator.rail.cstic.closure.KA-M-konisch.name",
                  description: "configurator.rail.cstic.closure.KA-M-konisch.description",
                  imageUrl: "configurator.rail.cstic.closure.KA-M-konisch.imageUrl",
                  info: "configurator.rail.cstic.closure.KA-M-konisch.info",
                  cstics: [
                    {
                      csticName: RailCstic.ClosingPlugs,
                      value: "KA..M KONISCH",
                    },
                  ],
                },
                {
                  name: "configurator.rail.cstic.closure.KA-ST/B.name",
                  description: "configurator.rail.cstic.closure.KA-ST/B.description",
                  imageUrl: "configurator.rail.cstic.closure.KA-ST/B.imageUrl",
                  info: "configurator.rail.cstic.closure.KA-ST/B.info",
                  cstics: [
                    {
                      csticName: RailCstic.ClosingPlugs,
                      value: "KA..ST/B",
                    },
                  ],
                },
                {
                  name: "configurator.rail.cstic.closure.WITHOUT.name",
                  cstics: [
                    {
                      csticName: RailCstic.ClosingPlugs,
                      value: "/",
                    },
                    [
                      {
                        condition: ({ mainKmat }) => {
                          return mainKmat.cstics[RailCstic.GuidewayDesign]?.value === "ADE";
                        },
                        value: {
                          csticName: RailCstic.GuidewayDesign,
                          value: "/",
                        },
                      },
                    ],
                  ],
                },
              ],
            },
            {
              name: "configurator.rail.step.closure.section.cover.name",
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const val = mainKmat.cstics[RailCstic.GuidewayDesign]?.value;
                    return val === "ADE" || val === "/";
                  },
                  value: true,
                },
              ],
              tiles: [
                {
                  name: "configurator.rail.cstic.closure.ADE.name",
                  description: "configurator.rail.cstic.closure.ADE.description",
                  info: "configurator.rail.cstic.closure.ADE.info",
                  imageUrl: "configurator.rail.cstic.closure.ADE.imageUrl",
                  cstics: [
                    {
                      csticName: RailCstic.GuidewayDesign,
                      value: "ADE",
                    },
                    {
                      csticName: RailCstic.ClosingPlugs,
                      value: "/",
                    },
                  ],
                },
              ],
            },
            {
              name: "configurator.rail.step.closure.section.holes.name",
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    if (
                      mainKmat.cstics[RailCstic.GuidewayDesign]?.value === "ADE" ||
                      mainKmat.cstics[RailCstic.ClosingPlugs]?.value !== "/"
                    ) {
                      return true;
                    }
                    return false;
                  },
                  value: false,
                },
                {
                  condition: () => true,
                  value: true,
                },
              ],
              tiles: [
                {
                  name: "configurator.rail.cstic.closure.hole.chamfered.name",
                  info: "configurator.rail.cstic.closure.hole.chamfered.info",
                  imageUrl: "configurator.rail.cstic.closure.hole.chamfered.imageUrl",

                  cstics: [
                    {
                      csticName: RailCstic.CounterboreChamfered,
                      value: "J",
                    },
                    {
                      csticName: RailCstic.ClosingPlugs,
                      value: "/",
                    },
                    [
                      {
                        condition: ({ mainKmat }) => {
                          return mainKmat.cstics[RailCstic.GuidewayDesign]?.value === "ADE";
                        },
                        value: {
                          csticName: RailCstic.GuidewayDesign,
                          value: "/",
                        },
                      },
                    ],
                  ],
                },
                {
                  name: "configurator.rail.cstic.closure.hole.counterbored.name",
                  info: "configurator.rail.cstic.closure.hole.counterbored.info",
                  imageUrl: "configurator.rail.cstic.closure.hole.counterbored.imageUrl",

                  cstics: [
                    {
                      csticName: RailCstic.CounterboreChamfered,
                      value: "N",
                    },
                    {
                      csticName: RailCstic.ClosingPlugs,
                      value: "/",
                    },
                    [
                      {
                        condition: ({ mainKmat }) => {
                          return mainKmat.cstics[RailCstic.GuidewayDesign]?.value === "ADE";
                        },
                        value: {
                          csticName: RailCstic.GuidewayDesign,
                          value: "/",
                        },
                      },
                    ],
                  ],
                },
              ],
            },
          ],
          tilesPerRow: 6,

          inputCstics: {
            ["Closing Plugs"]: {
              name: RailCstic.ClosingPlugs,
              required: true,
            },
            ["Guideway Design"]: {
              name: RailCstic.GuidewayDesign,
              required: true,
            },
            ["Counterbore Chamfered"]: {
              name: RailCstic.CounterboreChamfered,
              required: true,
            },
          },

          csticsToReset: [RailCstic.ClosingPlugs, RailCstic.GuidewayDesign, RailCstic.CounterboreChamfered],
        },
      ],
    },
    {
      name: "configurator.rail.page.4.name",
      enabled: true,
      resetLabel: RESET_STANDARD_AND_LENGTH,
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.LocatingFace,
          visible: true,

          name: "configurator.rail.step.locatingFace.name",
          description: "configurator.rail.step.locatingFace.description",
          info: "configurator.rail.step.locatingFace.info",

          tiles: [
            {
              name: "configurator.rail.cstic.locatingFace.O.name",
              imageUrl: "configurator.rail.cstic.locatingFace.O.image",
              cstics: [
                {
                  csticName: RailCstic.LocatingFace,
                  value: "O",
                },
              ],
            },
            {
              name: "configurator.rail.cstic.locatingFace.U.name",
              imageUrl: "configurator.rail.cstic.locatingFace.U.image",
              cstics: [
                {
                  csticName: RailCstic.LocatingFace,
                  value: "U",
                },
              ],
            },
          ],

          csticsToReset: [RailCstic.LocatingFace],
        },
        {
          type: SubstepType.ConfigureRail,
          visible: true,

          name: "configurator.rail.page.4.name",

          extraTexts: {
            upperMessage: "configurator.rail.step.guidewaySet.infoMessage.upper",
            lowerMessage: "configurator.rail.step.guidewaySet.infoMessage.lower",
            lengthName: "configurator.rail.cstic.guidewayLength.name", // TODO - Move to cstic
            lengthInfo: "configurator.rail.cstic.guidewayLength.info", // TODO - Move to cstic
            alarName: "configurator.rail.section.alar.name",
            alarInfo: "configurator.rail.section.alar.info",
            alarRecommended: "configurator.rail.section.alar.recommended",
            alarWarningMessage: "configurator.rail.section.alar.warning",
            partsSectionName: "configurator.rail.section.parts.name",
            partsSectionInfo: "configurator.rail.section.parts.info",
          },

          inputCstics: {
            [InputCstics.TotalLength]: {
              name: RailCstic.GuidewayLength,
              required: true,
            },
            [InputCstics.aL]: {
              name: RailCstic.AL,
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    return getCsticNumericValue(mainKmat, RailCstic.GuidewayLength) > 0;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
            [InputCstics.aR]: {
              name: RailCstic.AR,
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    return getCsticNumericValue(mainKmat, RailCstic.GuidewayLength) > 0;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
            [InputCstics.partSizingType]: {
              name: RailCstic.PartSizing,
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    return getCsticNumericValue(mainKmat, RailCstic.GuidewayLength) > 0;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
            [InputCstics.partAmount]: {
              name: RailCstic.PartAmount,
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    return !mainKmat.cstics[RailCstic.PartSizing]?.value;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    return getCsticNumericValue(mainKmat, RailCstic.GuidewayLength) > 0;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
            [InputCstics.partOne]: {
              name: RailCstic.PartOneLength,
              required: true,
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [partSizingType] = getCsticsValue(mainKmat, RailCstic.PartSizing);
                    return partSizingType === "J" && getCsticNumericValue(mainKmat, RailCstic.PartAmount) >= 2;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
            [InputCstics.partTwo]: {
              name: RailCstic.PartTwoLength,
              required: true,
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [partSizingType] = getCsticsValue(mainKmat, RailCstic.PartSizing);
                    return partSizingType === "J" && getCsticNumericValue(mainKmat, RailCstic.PartAmount) >= 2;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    return getCsticNumericValue(mainKmat, RailCstic.PartAmount) === 2;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
            [InputCstics.partThree]: {
              name: RailCstic.PartThreeLength,
              required: true,
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [partSizingType] = getCsticsValue(mainKmat, RailCstic.PartSizing);
                    return partSizingType === "J" && getCsticNumericValue(mainKmat, RailCstic.PartAmount) >= 3;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    return getCsticNumericValue(mainKmat, RailCstic.PartAmount) === 3;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
            [InputCstics.partFour]: {
              name: RailCstic.PartFourLength,
              required: true,
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [partSizingType] = getCsticsValue(mainKmat, RailCstic.PartSizing);
                    return partSizingType === "J" && getCsticNumericValue(mainKmat, RailCstic.PartAmount) >= 4;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    return getCsticNumericValue(mainKmat, RailCstic.PartAmount) === 4;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
            [InputCstics.partFive]: {
              name: RailCstic.PartFiveLength,
              required: true,
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [partSizingType] = getCsticsValue(mainKmat, RailCstic.PartSizing);
                    return partSizingType === "J" && getCsticNumericValue(mainKmat, RailCstic.PartAmount) >= 5;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => {
                    return getCsticNumericValue(mainKmat, RailCstic.PartAmount) === 5;
                  },
                  value: true,
                },
                {
                  condition: () => true,
                  value: false,
                },
              ],
            },
          },

          helperCstics: {
            [HelperCstics.totalLengthMin]: {
              name: HelperCstics.totalLengthMin,
            },
            [HelperCstics.totalLengthMax]: {
              name: HelperCstics.totalLengthMax,
            },
            [HelperCstics.alHardMax]: {
              name: RailCstic.AlarHardMax,
            },
            [HelperCstics.arHardMax]: {
              name: RailCstic.AlarHardMax,
            },
            [HelperCstics.alRecommendedMin]: {
              name: RailCstic.AlarMinRecommended,
            },
            [HelperCstics.alRecommendedMax]: {
              name: RailCstic.AlarMaxRecommended,
            },
            [HelperCstics.arRecommendedMin]: {
              name: RailCstic.AlarMinRecommended,
            },
            [HelperCstics.arRecommendedMax]: {
              name: RailCstic.AlarMaxRecommended,
            },
          },

          extraConfig: {
            [ExtraConfig.AlarHelperTextFactory]: ({
              hardMin = 0,
              hardMax = 0,
              recommendedMin = 0,
              recommendedMax = 0,
              t,
              value,
            }: AlarHelperTextFactoryParams) => {
              if (value === null) {
                return "";
              }
              if (value <= hardMin) {
                return "<=0mm";
              }
              if (value > hardMax) {
                return `>${hardMax}mm`;
              }

              if (value < recommendedMin || value > recommendedMax) {
                return (
                  `${t("common.recommended")}\n` +
                  `${t("common.min")} ${recommendedMin} / ${t("common.max")} ${recommendedMax}`
                );
              }

              return "";
            },
            [ExtraConfig.showNotRecommendedWarning]: true,
          },

          csticsToReset: [
            // TODO...
          ],
        },
      ],
    },
    {
      name: "configurator.rail.page.5.name",
      enabled: true,
      resetLabel: "",
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.SummaryPage,
          visible: true,

          info: [
            {
              condition: ({ mainKmat }) => {
                return mainKmat.cstics[RailCstic.LocatingFace]?.value === "U" && userIsCustomer();
              },
              value: "configurator.rail.summary.info.below.business",
            },
            {
              condition: ({ mainKmat }) => {
                return mainKmat.cstics[RailCstic.LocatingFace]?.value === "O" && userIsCustomer();
              },
              value: "configurator.rail.summary.info.above.business",
            },
            {
              condition: ({ mainKmat }) => {
                return mainKmat.cstics[RailCstic.LocatingFace]?.value === "U";
              },
              value: "configurator.rail.summary.info.below.plus",
            },
            {
              condition: ({ mainKmat }) => {
                return mainKmat.cstics[RailCstic.LocatingFace]?.value === "O";
              },
              value: "configurator.rail.summary.info.above.plus",
            },
          ],
        },
      ],
    },
  ],
};
